import { z } from "zod";
import { nanoidSchema } from "../utility/index.js";
import { nanoid } from "nanoid";
const headerLinkBaseSchema = z.object({
  uid: nanoidSchema,
  title: z.string(),
  show: z.boolean().default(true),
  isStarred: z.boolean().default(false),
  type: z.union([
    z.literal("guide"),
    z.literal("reference"),
    z.literal("link"),
    z.literal("group"),
    z.literal("version-selector"),
    z.literal("spacer")
  ]).default("link")
});
const headerLinkReferenceSchema = headerLinkBaseSchema.extend({
  type: z.literal("reference"),
  /** Whether the reference has a permalinked reference or not */
  hasPermalink: z.boolean().default(false).describe("Whether the reference has a permalinked reference or not"),
  hasManagedDoc: z.boolean().default(false).describe("Whether the reference has a linked registry API")
});
const headerLinkGuideSchema = headerLinkBaseSchema.extend({
  type: z.literal("guide")
});
const headerLinkVersionSelectorSchema = headerLinkBaseSchema.extend({
  type: z.literal("version-selector")
});
const headerLinkSpacerSchema = headerLinkBaseSchema.extend({
  type: z.literal("spacer")
});
const headerLinkExternalSchema = headerLinkBaseSchema.extend({
  type: z.literal("link"),
  isButton: z.boolean().default(false),
  link: z.string()
});
const headerLinkGroupSchema = headerLinkBaseSchema.extend({
  type: z.literal("group"),
  /** Children of the group. Groups cannot contain other groups, spacers, or the version selector */
  children: z.array(
    z.union([
      headerLinkGuideSchema,
      headerLinkReferenceSchema,
      headerLinkExternalSchema
    ])
  ).describe(
    "Children of the group. Groups cannot contain other groups, spacers, or the version selector"
  )
});
const headerLinkSchema = z.union([
  headerLinkGuideSchema,
  headerLinkReferenceSchema,
  headerLinkGroupSchema,
  headerLinkExternalSchema,
  headerLinkVersionSelectorSchema,
  headerLinkSpacerSchema
]);
function getHeaderLinks({
  guides,
  references,
  header
}) {
  const complete = [...header].filter((h) => {
    if (h.type === "guide") return !!guides[h.uid];
    if (h.type === "reference") return !!references[h.uid];
    return true;
  }).map((header2) => {
    if (header2.type !== "group") return header2;
    return {
      ...header2,
      // Only include header children if the guide/reference is available
      children: header2.children.filter((c) => {
        if (c.type === "guide") return !!guides[c.uid];
        if (c.type === "reference") return !!references[c.uid];
        return true;
      })
    };
  });
  const orderedIds = [];
  complete.forEach((h) => {
    orderedIds.push(h.uid);
    if (h.type === "group") {
      orderedIds.push(...h.children.map((c) => c.uid));
    }
  });
  Object.values(guides).forEach((g) => {
    if (!orderedIds.includes(g.uid)) {
      complete.push({
        uid: g.uid,
        title: g.title,
        show: g.show,
        isStarred: g.isStarred,
        type: "guide"
      });
    }
  });
  Object.values(references).forEach((r) => {
    if (!orderedIds.includes(r.uid)) {
      complete.push({
        uid: r.uid,
        title: r.title,
        show: r.show,
        isStarred: r.isStarred,
        type: "reference",
        hasPermalink: !!r.specPermalink,
        hasManagedDoc: !!r.managedDoc
      });
    }
  });
  if (!complete.find((e) => e.type === "version-selector")) {
    complete.push(
      {
        uid: nanoid(),
        type: "spacer",
        title: "",
        show: false,
        isStarred: false
      },
      {
        uid: nanoid(),
        title: "Version Selector",
        type: "version-selector",
        show: true,
        isStarred: false
      }
    );
  }
  complete.forEach((element) => {
    if (element.type === "guide") {
      element.title = guides[element.uid].title;
      element.show = guides[element.uid].show;
      element.isStarred = guides[element.uid].isStarred;
    }
    if (element.type === "reference") {
      element.title = references[element.uid].title;
      element.show = references[element.uid].show;
      element.isStarred = references[element.uid].isStarred;
      element.hasPermalink = !!references[element.uid].specPermalink;
      element.hasManagedDoc = !!references[element.uid].managedDoc;
    }
    if (element.type === "group") {
      element.children.forEach((c) => {
        if (c.type === "guide") {
          c.title = guides[c.uid].title;
          c.show = guides[c.uid].show;
          c.isStarred = guides[c.uid].isStarred;
        }
        if (c.type === "reference") {
          c.title = references[c.uid].title;
          c.show = references[c.uid].show;
          c.isStarred = references[c.uid].isStarred;
          c.hasPermalink = !!references[c.uid].specPermalink;
          c.hasManagedDoc = !!references[c.uid].managedDoc;
        }
      });
    }
  });
  return complete;
}
export {
  getHeaderLinks,
  headerLinkSchema
};
//# sourceMappingURL=header-links.js.map
